import React, { FC, ChangeEvent, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Card,
    Grid,
    Table,
    Tooltip,
    Divider,
    useTheme,
    Collapse,
    TableRow,
    TableBody,
    TableCell,
    TableHead,
    IconButton,
    TableContainer,
    TablePagination,
    SelectChangeEvent,
    CircularProgress,
    Dialog,
    DialogContent,
} from '@mui/material';
import moment from 'moment';

import RoomIcon from '@mui/icons-material/Room';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import InsightsTwoToneIcon from '@mui/icons-material/InsightsTwoTone';
import AttachmentTwoToneIcon from '@mui/icons-material/AttachmentTwoTone';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';
import { getActivityStyle } from 'src/util/activity-color';

interface Column {
    label: string;
    field: string;
    type?: string;
    width?: number;
}

interface NavigateActions {
    onClickEdit?: (id: string) => void;
    onClickDelete?: (id: string) => void;
    onClickDetails?: (id: string) => void;
    onClickSavePdf?: (id: string) => void;
    onClickHyperlink?: (id: string, row: any) => void;
    onClickUploadDocument?: (id: string, row: any) => void;
    onClickTracking?: (id: string, row: any) => void;
    onClickPage?: (page: number, limit: number) => void;
    handleDropdownChange?: (e: SelectChangeEvent<string>, label: string) => void;
}

interface CustomTableProps {
    data: any[];
    title: string
    columns: Column[];
    viewOnly?: boolean
    className?: string;
    isSavePdf?: boolean
    isLoading?: boolean;
    hideAction?: boolean
    isCollapsible?: boolean;
    alternateColor?: boolean;
    noPaging?: boolean;
    navigateAction?: NavigateActions
    collapsibleContent?: React.ReactNode;
    cardHeaderChildren?: React.ReactNode;
    sortConfig?: { key: string; direction: 'asc' | 'desc' };
    getCollapsibleContent?: (id: string, row?: any) => React.ReactNode;
    setSortConfig?: (config: { key: string; direction: 'asc' | 'desc' }) => void;
    pagination?: {
        page: number;
        limit: number;
        total: number;
    }
}

const CustomTable: FC<CustomTableProps> = ({
    data,
    columns,
    viewOnly,
    pagination,
    sortConfig,
    setSortConfig,
    navigateAction,
    isLoading = false,
    isSavePdf = false,
    cardHeaderChildren,
    hideAction = false,
    getCollapsibleContent,
    isCollapsible = false,
    alternateColor = false,
    noPaging = false,
}) => {

    const theme = useTheme();
    const [page, setPage] = useState<number>(pagination?.page || 0);
    const [limit, setLimit] = useState<number>(pagination?.limit || 0);
    const [openRow, setOpenRow] = useState<string | null>(null);

    useEffect(() => {
        if (!pagination) return;
        setPage(pagination.page);
        setLimit(pagination.limit);
    }, [pagination]);

    const handleSort = (columnKey: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === columnKey && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        if (setSortConfig) {
            setSortConfig({ key: columnKey, direction });
        }
    };

    const getSortIcon = (columnKey: string) => {
        if (!sortConfig || sortConfig.key !== columnKey) return null;
        return sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
    };

    const handlePageChange = (event: any, newPage: number): void => {
        setPage(newPage);
        navigateAction?.onClickPage(newPage, limit);
    };

    const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const newLimit = parseInt(event.target.value);
        setLimit(newLimit);
        navigateAction?.onClickPage(page, newLimit);
    };

    const handleRowClick = (id: string) => {
        setOpenRow(openRow === id ? null : id);
    };



    return (
        <Card>
            <Dialog open={isLoading} PaperProps={{ style: { backgroundColor: 'transparent', boxShadow: 'none' } }}>
                <Box display="flex" justifyContent="center" alignItems="center">
                    <DialogContent>
                        <CircularProgress />
                    </DialogContent>
                </Box>
            </Dialog>
            <>
                {cardHeaderChildren}
                <Divider />
                <TableContainer>
                    <Table>
                        <TableHead sx={{ background: alternateColor ? theme.colors.ckb.alternate : '#CCE1E0' }}>
                            <TableRow>
                                {isCollapsible && <TableCell />}
                                {columns.map((column) => (
                                    <TableCell
                                        sx={{ color: alternateColor ? theme.colors.ckb.secondaryAlternate : theme.colors.ckb.secondary, fontSize: '12px', whiteSpace: 'nowrap' }}
                                        key={column.field}
                                        style={{ width: column.width, cursor: 'pointer' }}
                                        onClick={() => handleSort(column.field)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {column.label}
                                            {getSortIcon(column.field)}
                                        </div>
                                    </TableCell>
                                ))}
                                {!viewOnly && !hideAction && <TableCell align="center">
                                    Actions
                                </TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                data.map((row: any, index: number) => {
                                    return (
                                        <React.Fragment key={row.id}>
                                            <TableRow hover key={row.id} selected={false}>
                                                {isCollapsible && (
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            color="inherit"
                                                            disableRipple
                                                            onClick={() => handleRowClick(row.id)}
                                                            style={{ transform: openRow == row.id ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                                                        >
                                                            <ArrowLeft fontSize="small" style={{ fill: alternateColor ? theme.colors.ckb.secondaryAlternate : theme.colors.ckb.primary }} />
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                                {columns.map((column) => (
                                                    <TableCell key={column.field} sx={{ fontSize: '11px', height: '3px', width: column.width }}>
                                                        {column.type === 'date'
                                                            ? moment(row[column.field]).format('MM/DD/YYYY HH:mm')
                                                            : column.type === 'hyperlink'
                                                                ? <div
                                                                    onClick={() => navigateAction.onClickHyperlink(row[column.field], row)}
                                                                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                                                                    <a style={{ color: '#3F69BF', cursor: 'pointer' }}>{row[column.field]}</a>
                                                                    <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#3F69BF' }}>
                                                                        <ChevronRightIcon />
                                                                    </div>
                                                                </div>
                                                                : column.field === 'sn_number'
                                                                    ? (() => {
                                                                        if (!row[column.field]) {
                                                                            return null;
                                                                        }
                                                                        const numbers = row[column.field].split(',');
                                                                        const displayedNumbers = numbers.slice(0, 2).join(', ');
                                                                        const moreCount = numbers.length - 2;
                                                                        const moreNumbers = numbers.slice(2).join(', ');
                                                                        return (
                                                                            <>
                                                                                {displayedNumbers}
                                                                                {moreCount > 0 && (
                                                                                    <Tooltip title={moreNumbers} arrow>
                                                                                        <span style={{ color: '#3F69BF', cursor: 'pointer' }}>
                                                                                            {` +${moreCount} more`}
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    })()
                                                                    : column.label === 'Last Activities'
                                                                        ? (() => {
                                                                            return (
                                                                                <div style={row[column.field] === null ? { display: 'none' } : getActivityStyle(row[column.field])}>
                                                                                    {row[column.field]}
                                                                                </div>
                                                                            );
                                                                        })()
                                                                        : column.field === 'no'
                                                                            ? index + 1
                                                                            : row[column.field]}
                                                    </TableCell>
                                                ))}
                                                {!viewOnly && !hideAction && <TableCell align="right">
                                                    {
                                                        navigateAction?.onClickSavePdf && (
                                                            <Tooltip sx={{ display: isSavePdf ? 'block' : 'none' }} title="Save PDF" arrow>
                                                                <IconButton
                                                                    sx={{
                                                                        '&:hover': {
                                                                            background: theme.colors.warning.lighter
                                                                        },
                                                                        color: theme.colors.warning.main,
                                                                        display: isSavePdf ? 'block' : 'none'
                                                                    }}
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={() => navigateAction?.onClickSavePdf(row.id)}
                                                                >
                                                                    <DescriptionTwoToneIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )
                                                    }

                                                    {
                                                        navigateAction?.onClickDetails && !hideAction && (
                                                            <Tooltip title="Details" arrow>
                                                                <IconButton
                                                                    sx={{
                                                                        '&:hover': {
                                                                            background: theme.colors.success.lighter
                                                                        },
                                                                        color: theme.colors.success.main
                                                                    }}
                                                                    color="inherit"
                                                                    size="small"
                                                                    onClick={() => navigateAction?.onClickDetails(row.id)}
                                                                >
                                                                    <InsightsTwoToneIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )
                                                    }

                                                    {
                                                        navigateAction?.onClickEdit && !hideAction && (
                                                            <Tooltip title="Edit" arrow>
                                                                <IconButton
                                                                    sx={{
                                                                        '&:hover': {
                                                                            background: theme.colors.primary.lighter
                                                                        },
                                                                        color: theme.palette.primary.main
                                                                    }}
                                                                    color="inherit"
                                                                    size="small"
                                                                    onClick={() => navigateAction?.onClickEdit(row.id)}
                                                                >
                                                                    <EditTwoToneIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )
                                                    }

                                                    <Grid container gap={1} sx={{ flexWrap: 'nowrap' }}>
                                                        {
                                                            navigateAction?.onClickTracking && !hideAction && (
                                                                <Tooltip title="Show Tracking" arrow>
                                                                    <Grid item md={6}>
                                                                        <IconButton
                                                                            sx={{
                                                                                '&:hover': {
                                                                                    background: theme.colors.ckb.secondary,

                                                                                },
                                                                                color: 'white',
                                                                                background: theme.colors.ckb.primary

                                                                            }}
                                                                            color="primary"
                                                                            size="small"
                                                                            onClick={() => navigateAction?.onClickTracking(row.id, row)}
                                                                        >
                                                                            <RoomIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Grid>
                                                                </Tooltip>

                                                            )
                                                        }
                                                        {
                                                            navigateAction?.onClickUploadDocument && !hideAction && (
                                                                <Tooltip title="Upload Document" arrow>
                                                                    <Grid item md={6}>
                                                                        <IconButton
                                                                            sx={{
                                                                                '&:hover': {
                                                                                    background: theme.colors.primary.light
                                                                                },
                                                                                // color: 'white',
                                                                                border: row.attachments.length > 0 ? '1px solid #3F69BF' : undefined,
                                                                                color: row.attachments.length == 0 ? 'white' : theme.colors.primary.dark,
                                                                                background: row.attachments.length > 0 ? 'white' : theme.colors.primary.dark,

                                                                            }}
                                                                            color="primary"
                                                                            size="small"
                                                                            onClick={() => navigateAction?.onClickUploadDocument(row.id, row)}
                                                                        >
                                                                            <AttachmentTwoToneIcon fontSize="small" />
                                                                            {
                                                                                row.attachments.length > 0 && (
                                                                                    <a style={{ fontSize: 12, marginLeft: 3 }}> {row.attachments.length > 0 && row.attachments.length}</a>
                                                                                )
                                                                            }

                                                                        </IconButton>
                                                                    </Grid>
                                                                </Tooltip>
                                                            )
                                                        }
                                                    </Grid>

                                                    {
                                                        navigateAction?.onClickDelete && !hideAction && (
                                                            <Tooltip title="Delete" arrow>
                                                                <IconButton
                                                                    sx={{
                                                                        '&:hover': { background: theme.colors.error.lighter },
                                                                        color: theme.palette.error.main
                                                                    }}
                                                                    color="inherit"
                                                                    size="small"
                                                                    onClick={() => navigateAction?.onClickDelete(row.id)}
                                                                >
                                                                    <DeleteTwoToneIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )
                                                    }
                                                </TableCell>
                                                }
                                            </TableRow>
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + 2}>
                                                    {isCollapsible && (
                                                        <Collapse in={openRow == row.id} timeout="auto" unmountOnExit>
                                                            <Box p={2}>
                                                                {(getCollapsibleContent && openRow == row.id) ? getCollapsibleContent(openRow, row) : null}
                                                            </Box>
                                                        </Collapse>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {
                    pagination && !noPaging && (

                        <Box p={2}>
                            <TablePagination
                                page={page}
                                component="div"
                                rowsPerPage={limit}
                                count={pagination.total}
                                onPageChange={handlePageChange}
                                rowsPerPageOptions={[5, 10, 25, 30]}
                                onRowsPerPageChange={handleLimitChange}
                            />
                        </Box>
                    )
                }
            </>
        </Card>
    );
};

CustomTable.propTypes = {
    //@ts-ignore
    columns: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        field: PropTypes.string.isRequired,
        type: PropTypes.string, // Optional
        width: PropTypes.number, // Optional
    })).isRequired,
    data: PropTypes.array.isRequired
};

CustomTable.defaultProps = {
    columns: [],
    data: []
};

export default CustomTable;
